import { useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { CommunicationPatientRef } from '../../../../services/communications';
import { LinkedPatientRow } from './LinkedPatientRow';
import { MetaCard } from './MetaCard';
import { PatientPicker } from './PatientPicker';

interface LinkedPatientsCardProps {
    practiceId: string | null;
    patients: CommunicationPatientRef[];
    onChange: (next: CommunicationPatientRef[]) => void;
    /** The create page titles this card "Patients" instead - see CommunicationFormMetaRail.tsx. */
    title?: string;
}

// A controlled picker, not a data source: only calls `onChange`, never
// touches the network. The create form batches until save; the detail
// page's rail (CommunicationMetaRail.tsx) PATCHes immediately.
export function LinkedPatientsCard({
    practiceId,
    patients,
    onChange,
    title = 'Linked patients'
}: LinkedPatientsCardProps) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const linkButtonRef = useRef<HTMLButtonElement>(null);

    // Closes after a single pick, matching the category dropdown - adding
    // another patient means tapping "+ Link a patient" again.
    const handlePick = (patient: CommunicationPatientRef) => {
        setPickerOpen(false);
        if (patients.some((p) => p.id === patient.id)) return;
        onChange([...patients, patient]);
    };

    const handleRemove = (patientId: string) => {
        onChange(patients.filter((p) => p.id !== patientId));
    };

    return (
        <MetaCard title={title}>
            <div className="flex flex-col gap-2.5">
                {patients.length === 0 ? (
                    <p className="text-[12px] text-gray-400">No patients linked yet</p>
                ) : (
                    <div className="flex flex-col gap-1.5">
                        {patients.map((p) => (
                            <LinkedPatientRow
                                key={p.id}
                                patient={p}
                                onRemove={() => handleRemove(p.id)}
                            />
                        ))}
                    </div>
                )}
                <button
                    ref={linkButtonRef}
                    type="button"
                    disabled={!practiceId}
                    title={practiceId ? undefined : 'Select a practice first'}
                    onClick={() => setPickerOpen((v) => !v)}
                    className="w-full h-9 rounded-lg border border-dashed border-gray-300 hover:border-gray-800 flex items-center justify-center gap-1.5 text-[12px] font-semibold text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-500"
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Link a patient
                </button>
                {!practiceId && (
                    <p className="text-[11px] text-gray-400">Select a practice first.</p>
                )}
                {practiceId && pickerOpen && (
                    <PatientPicker
                        triggerRef={linkButtonRef}
                        practiceId={practiceId}
                        linkedIds={patients.map((p) => p.id)}
                        onPick={handlePick}
                        onClose={() => setPickerOpen(false)}
                    />
                )}
            </div>
        </MetaCard>
    );
}
